import { Box, Button, InputAdornment, SvgIcon, TextField, Typography } from '@mui/material';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { Page } from 'src/components/page';
import { useAuth, useDialog, useResources } from '../../../hooks';
import { deleteLocation, getLocations } from '../../../thunks/location-thunks';
import { DataTable } from '../../data-table';
import { DataTableExtendedFilter } from '../../data-table/data-table-extended-filter';
import { DataTableFooter } from '../../data-table/data-table-footer';
import { ConfirmDialog } from '../../dialogs';
import { Icons, PlusIcon } from '../../icons';
import { OrganizationsSelect } from '../../organizations';
import { LocationsEditDialog } from '../locations-edit-dialog';
import { LocationsTableActions } from '../locations-table-actions';

export const LocationsList: ComponentType = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { isSuperAdmin } = useAuth();
  const [openLocationsEdit, closeLocationsEdit] = useDialog(LocationsEditDialog);
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'location',
    onGetAll: getLocations,
    defaultParams: { embed: 'region,organization' },
  });

  const handleCreate = () => {
    openLocationsEdit({
      title: t('locations.add', 'Добавить офис'),
      onSubmit: () => {
        closeLocationsEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleEdit = (id: string | number) => {
    openLocationsEdit({
      title: t('locations.edit', 'Редактировать офис'),
      id,
      onSubmit: () => {
        closeLocationsEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const handleDelete = (id: string | number) => {
    openConfirm({
      size: 'small',
      resolve: () => {
        closeConfirm();
        deleteLocation(id)(dispatch).then(() => {
          handleFetchFirstPage();
        });
      },
    });
  };

  const data = {
    hits,
    isLoading,
    columns: [
      {
        width: 60,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 384,
        label: 'Название',
        labelKey: 'location.name.label',
        renderItem: (item: Record<string, any>) => getTranslatedValue(item.name, i18n.language),
      },
      {
        label: 'Адрес',
        labelKey: 'location.address.label',
        renderItem: (item: Record<string, any>) => getTranslatedValue(item.address, i18n.language),
      },
      {
        width: isSuperAdmin ? 300 : 'auto',
        label: 'Регион',
        labelKey: 'region',
        renderItem: (item: Record<string, any>) =>
          getTranslatedValue(item.region.title, i18n.language),
      },
      {
        width: 'auto',
        label: 'Организация',
        labelKey: 'organization',
        hidden: !isSuperAdmin,
        renderItem: (item: Record<string, any>) =>
          getTranslatedValue(item.organization.title, i18n.language),
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <LocationsTableActions item={item} onEdit={handleEdit} onDelete={handleDelete} />
        ),
      },
    ],
  };

  return (
    <Page
      title={t('locations', 'Офисы')}
      topButton={
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('locations.add', 'Добавить офис')}
        </Button>
      }
    >
      <DataTableExtendedFilter
        {...{
          initialValues: query,
          onSubmit: handleQueryUpdate,
        }}
      >
        {(formik) => {
          return (
            <>
              <TextField
                {...formik.getFieldProps('search')}
                label={t('search', 'Поиск')}
                placeholder={t('search', 'Поиск')}
                size="small"
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon component={Icons.Search} viewBox="0 0 24 24" color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
              {isSuperAdmin && (
                <Box>
                  <OrganizationsSelect
                    {...formik.getFieldProps('organization')}
                    label={t('profile.organization', 'Организация')}
                    placeholder={t('profile.organization.placeholder', 'Выберите организацию')}
                    size="small"
                    prompt={t('all', 'Все')}
                    sx={{ width: 300 }}
                  />
                </Box>
              )}
            </>
          );
        }}
      </DataTableExtendedFilter>
      <DataTable {...data} />
      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </Page>
  );
};
